import React from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../ui';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartBackgroundColor, getChartJsOptions, getChartColors } from '../../utils/themeHelpers';
import { getResponsiveXAxisTicks, isMobileChartViewport } from '../../utils/chartResponsive';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import { formatNumber } from '../../utils/formatters';
import { CHART_GUIDES } from '../../constants/chartGuides';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import {
  activeSeries,
  getMobileGroupLabels,
  getControllerButtonClass,
  getSymmetricAxisBounds,
  getSortedPurchaseData,
  getZeroLineGrid,
  numeric,
  toGrowthLabel,
  useCustomerPurchaseBehaviorData,
  valueLabelPlugin,
  CustomerPurchaseBehaviorChartProps,
} from './customerPurchaseBehaviorData';

const CustomerPurchaseBehaviorCountChangeByGroupChart: React.FC<CustomerPurchaseBehaviorChartProps> = ({
  branchId,
  purchaseData,
  onRetry,
}) => {
  const { currentTheme } = useTheme();
  const chartColors = getChartColors(currentTheme);
  const countChangeByGroupChartRef = React.useRef<HTMLDivElement>(null);
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const [selectedTransitionLabel, setSelectedTransitionLabel] = React.useState<string>('');
  const { data: fetchedData, isLoading, error, refetch } = useCustomerPurchaseBehaviorData(branchId, {
    enabled: purchaseData === undefined,
  });
  const data = purchaseData ?? fetchedData;
  const showLoading = purchaseData === undefined && isLoading;
  const queryError = purchaseData === undefined ? error : null;
  const retry = onRetry ?? refetch;
  const sortedData = getSortedPurchaseData(data);
  const groupLabelsWithTotal = getMobileGroupLabels([
    ...activeSeries.map((series) => series.label),
    'کل سفارش‌ها',
  ]);
  const isMobileChart = isMobileChartViewport();

  // برای هر گذار ماه‌به‌ماه، تغییر تعداد فاکتورهای هر گروه نسبت به ماه قبل را نگه می‌داریم.
  const monthlyCountChanges = sortedData.slice(1).map((currentItem, idx) => {
    const previousItem = sortedData[idx];
    const groupCountValues = activeSeries.map((series) => (
      numeric(currentItem[series.key]) - numeric(previousItem[series.key])
    ));
    const currentTotal = activeSeries.reduce((sum, series) => sum + numeric(currentItem[series.key]), 0);
    const previousTotal = activeSeries.reduce((sum, series) => sum + numeric(previousItem[series.key]), 0);
    return {
      transitionLabel: toGrowthLabel(previousItem.YearMonth, currentItem.YearMonth),
      countValues: [...groupCountValues, currentTotal - previousTotal],
    };
  });
  const monthlyCountChangesLatestFirst = [...monthlyCountChanges].reverse();
  const defaultTransitionLabel = monthlyCountChangesLatestFirst[0]?.transitionLabel || '';

  React.useEffect(() => {
    if (!monthlyCountChangesLatestFirst.length) {
      return;
    }
    if (!selectedTransitionLabel) {
      setSelectedTransitionLabel(defaultTransitionLabel);
      return;
    }
    const stillExists = monthlyCountChangesLatestFirst.some(
      (item) => item.transitionLabel === selectedTransitionLabel
    );
    if (!stillExists) {
      setSelectedTransitionLabel(monthlyCountChangesLatestFirst[0].transitionLabel);
    }
  }, [monthlyCountChangesLatestFirst, defaultTransitionLabel, selectedTransitionLabel]);

  if (!branchId) {
    return (
      <div dir="rtl" className="card-base card-padding border border-status-warning" role="alert">
        برای دیدن این داشبورد، ابتدا شعبه را انتخاب کنید.
      </div>
    );
  }

  if (showLoading) {
    return <LoadingSpinner variant="dashboard" message="در حال بارگذاری رفتار خرید مشتریان…" />;
  }

  if (queryError) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage
          message="دریافت اطلاعات رفتار خرید مشتریان با خطا مواجه شد. لطفاً دوباره تلاش کنید."
          onRetry={() => {
            retry();
          }}
        />
      </div>
    );
  }

  if (!sortedData.length) {
    return (
      <div className="card-base card-padding">
        <EmptyState message="برای این شعبه هنوز اطلاعات رفتار خریدی ثبت نشده است" />
      </div>
    );
  }

  const selectedCountChange = monthlyCountChangesLatestFirst.find(
    (item) => item.transitionLabel === selectedTransitionLabel
  ) || monthlyCountChangesLatestFirst[0];
  const selectedCountValues = (selectedCountChange?.countValues || [])
    .filter((value) => value !== null && value !== undefined);
  const selectedGroupAxisBounds = getSymmetricAxisBounds(selectedCountValues, 6);

  return (
    <div ref={countChangeByGroupChartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId="customer-purchase-behavior-count-change-by-group"
        componentTitle="تغییر تعداد سفارش در هر گروه"
        componentType="chart"
        dashboardSource="customerPurchaseBehavior"
        onInfo={() => openGuide(CHART_GUIDES.purchaseBehaviorGroupCountChange)}
        onFavorite={() => handleFavoriteToggle({
          componentId: 'customer-purchase-behavior-count-change-by-group',
          componentTitle: 'تغییر تعداد سفارش در هر گروه',
          dashboardSource: 'customerPurchaseBehavior',
          componentType: 'chart',
          description: 'تغییر تعداد فاکتورهای همهٔ گروه‌های مشتری در یک ماه انتخابی نسبت به ماه قبل',
          props: { branchId },
        })}
        isFavorited={isFavorited('customer-purchase-behavior-count-change-by-group')}
        chartRef={countChangeByGroupChartRef}
        className="mb-4"
      />
      <h3 className="title-card">تغییر تعداد سفارش در هر گروه</h3>
      <div className="mb-4 border rounded-lg p-3 md:p-4 bg-slate-50/60">
        <div className="mb-2 text-xs md:text-sm font-semibold text-text-secondary text-center">
          انتخاب ماه
        </div>
        <div role="group" aria-label="انتخاب ماه مقایسه" className="cpb-month-picker mb-1 lg:flex-wrap lg:items-center lg:justify-center">
          {monthlyCountChangesLatestFirst.map((item) => {
            const isActive = item.transitionLabel === selectedCountChange?.transitionLabel;
            return (
              <button
                key={item.transitionLabel}
                type="button"
                onClick={() => setSelectedTransitionLabel(item.transitionLabel)}
                className={getControllerButtonClass(isActive, true)}
                title={`نمایش تغییر تعداد گروه‌ها در ${item.transitionLabel}`}
                aria-pressed={isActive}
              >
                {item.transitionLabel}
              </button>
            );
          })}
        </div>
      </div>
      {selectedCountChange && (
        <div className="border rounded-lg p-3 md:p-4">
          <h4 className="text-sm md:text-base font-semibold mb-3 text-text-primary text-center">
            {`تغییر تعداد گروه‌ها در ${selectedCountChange.transitionLabel}`}
          </h4>
          <div
            role="img"
            aria-label={`نمودار تغییر تعداد گروه‌ها در ${selectedCountChange.transitionLabel}`}
            className="mobile-chart-host mobile-chart-host--category-bars-tall w-full"
          >
            <Bar
              plugins={[...(isMobileChart ? [zeroBaselinePlugin as any] : [valueLabelPlugin as any, zeroBaselinePlugin as any])]}
              data={{
                labels: groupLabelsWithTotal,
                datasets: [
                  {
                    type: 'bar',
                    label: 'تغییر تعداد',
                    data: selectedCountChange.countValues,
                    backgroundColor: groupLabelsWithTotal.map((_, idx) => getChartBackgroundColor(idx, currentTheme, 0.75)),
                    borderColor: groupLabelsWithTotal.map((_, idx) => chartColors[idx % chartColors.length]),
                    borderWidth: 1,
                    borderRadius: 6,
                    barPercentage: 0.9,
                    categoryPercentage: 0.86,
                    maxBarThickness: 54,
                    isPercent: false,
                  } as any,
                ] as any,
              }}
              options={{
                ...getChartJsOptions(currentTheme),
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                  mode: 'index',
                  intersect: false,
                },
                scales: {
                  x: {
                    ...getChartJsOptions(currentTheme).scales?.x,
                    ticks: getResponsiveXAxisTicks(
                      getChartJsOptions(currentTheme).scales?.x?.ticks as Record<string, unknown> | undefined,
                    ),
                  },
                  y: {
                    ...getChartJsOptions(currentTheme).scales?.y,
                    grid: getZeroLineGrid(getChartJsOptions(currentTheme).scales?.y?.grid),
                    min: Math.floor(selectedGroupAxisBounds.min),
                    max: Math.ceil(selectedGroupAxisBounds.max),
                    ticks: {
                      ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                      precision: 0,
                      callback: (value: any) => formatNumber(Math.round(Number(value || 0))),
                      stepSize: selectedGroupAxisBounds.stepSize,
                    },
                    title: {
                      display: !isMobileChart,
                      text: 'تغییر تعداد سفارش نسبت به ماه قبل',
                    },
                  },
                },
                plugins: {
                  ...getChartJsOptions(currentTheme).plugins,
                  ...(!isMobileChart ? ({
                    valueLabelPlugin: {
                      minPixelHeight: 14,
                      minAbsValue: 1,
                      avoidOverlap: false,
                      labelPosition: 'center',
                      showBackground: false,
                      textColor: 'rgba(17, 24, 39, 0.92)',
                      fontSize: 11,
                    },
                  } as any) : {}),
                  legend: {
                    ...getChartJsOptions(currentTheme).plugins?.legend,
                    display: false,
                  },
                  tooltip: {
                    ...getChartJsOptions(currentTheme).plugins?.tooltip,
                    callbacks: {
                      title: (tooltipItems: any) => {
                        const titleLabel = tooltipItems?.[0]?.label || '';
                        return `${titleLabel} | ${selectedCountChange.transitionLabel}`;
                      },
                      label: (context: any) => {
                        const separator = ' : ';
                        const value = Number(context.parsed.y || 0);
                        const sign = value > 0 ? '‎+' : '';
                        return `${context.dataset.label}${separator}${sign}${formatNumber(value)}`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPurchaseBehaviorCountChangeByGroupChart;
